const en = {
  common: {
    loading: "Loading...",
    close: "Close",
    submit: "Submit",
    review: "Review",
    help: "Help",
    tour: "Tour",
    instructions: "Instructions",
    language: "Language",
    ok: "Ok",
    appLoaderText:
      "Loading... Please wait while we prepare your assessment environment."
  },
  assessment: {
    fetchError: "Error fetching the {{featureName}}",
    somethingWentWrong: "Something went wrong.",
    goBackToDashboard: "Go back to dashboard",
    confirmSubmissionTitle: "Confirm Submission",
    scheduleIdNotFound: "Schedule Id Not Found",
    ongoingIdNotFound: "Ongoing Id Not Found",
    errorFetchingOngoingData: "Error fetching ongoing data",
    connectionRestoredLabel: "Connection restored.",
    connectionRestoredDescription: "You can continue running test cases.",
    connectionLostLabel: "We have lost connection to the test server.",
    connectionLostDescription:
      "You will not be able to run or validate test cases right now.",
    featureNames: {
      assessment: "Assessment",
      knowledgeCheck: "Knowledge Check"
    },
    cardStatus: {
      na: "N/A",
      toBeLaunched: "To Be Launched",
      expired: "Expired",
      expiringSoon: "Expiring Soon",
      ongoing: "Ongoing"
    },
    cardState: {
      draft: "Draft",
      published: "Published",
      cancelled: "Cancelled",
      inreview: "In Review",
      readytopublish: "Ready to Publish",
      sentback: "Sent Back"
    },
    dashboard: {
      videoProctoring: "Video Proctoring",
      submitFeature: "Submit {{featureName}}",
      noSectionsFound: "No sections found",
      expandAllSections: "Expand All Sections",
      closeAllSections: "Close All Sections",
      continueShapingAssessments: "Continue Shaping Your Assessments",
      viewAll: "View All",
      legend: {
        notStarted: "Not Started",
        inProgress: "In Progress",
        completed: "Completed"
      }
    },
    confirmModal: {
      description:
        "Once submitted, you will not be able to make any changes to your {{featureName}}. Please review your answers before confirming. Do you want to proceed with the submission?",
      lowWordCountWarning:
        "Your answer for the question, {{questionTitle}} is less than 40 words. If you submit the assessment now, this response will not be saved.",
      videoNotSubmittedWarning:
        "Your recorded video for the question, {{questionTitle}} is not submitted. Submitting the assessment now will discard this answer.",
      unansweredQuestions: "You have {{count}} unanswered questions",
      andFlaggedQuestions: "and {{count}} flagged questions",
      failedToFinish: "Failed to finish the {{featureName}}"
    },
    feedback: {
      submittedTitle: "Your {{featureName}} has been submitted successfully!",
      submittedDescription:
        "Your answers have been submitted. We appreciate the time and effort you have put into completing this {{featureName}}!",
      resultInProgress:
        "Result is getting calculated and it may take up to a minute. Please wait...",
      resultReady: "Result is ready! Click on Show Result button to view it.",
      resultDelayed:
        "Result calculation is taking more time than usual. Please contact admin for your result.",
      showResult: "Show Result",
      redirectingToResult:
        "Redirecting to result page in {{seconds}} seconds if you do not click on Show Result button",
      shareFeedbackTitle: "Share Your Feedback",
      shareFeedbackDescription:
        "Your insights pave the way for growth. Let us level up together!",
      errorSubmittingFeedback: "Error Submitting Feedback",
      feedbackSubmittedTitle: "All Set, Feedback Is Submitted!",
      feedbackSubmittedDescription:
        "Your feedback has been submitted. Thank you for helping us improve the {{featureName}} experience!",
      feedbackAlreadySubmittedTitle: "All Set, Feedback Already Submitted!",
      feedbackAlreadySubmittedDescription:
        "Looks like you have already submitted the feedback. Thank you for helping us improve the {{featureName}} experience!"
    },
    page: {
      errorFetchingPreAssessmentData: "Error Fetching Pre Assessment Data",
      connectionRestoredLabel: "Connection restored.",
      connectionRestoredDescription: "Syncing your latest changes...",
      offlineLabel: "Looks like you are offline.",
      offlineDescription: "Changes will not sync until you reconnect"
    },
    question: {
      questions: "Questions",
      closeQuestionsPanel: "Close questions panel",
      collapse: "Collapse",
      sectionSummary: "Section Summary",
      sections: "Sections",
      toggleQuestionsPanel: "Toggle questions panel",
      switchModal: {
        switchQuestionButton: "Switch Question",
        leaveHeading: "Are you sure you want to leave?",
        discardMessage:
          "You have unsaved changes. If you change the question, your response will be permanently deleted.",
        saveChangesButton: "Save Changes",
        saveHeading: "Do you want to save the changes?",
        saveBeforeSwitchMessage:
          "You have unsaved changes. Would you like to save them before changing the question?"
      }
    },
    faq: {
      q1: {
        question: "What should I do before starting the {{featureName}}?",
        firstSentence: "Please ensure you've completed the following:",
        list: {
          item1: "A stable internet connection",
          item2:
            "A working webcam and microphone (if video proctoring is enabled)",
          item3: "A quiet, distraction-free environment",
          item4:
            "If video proctoring is enabled, you'll be prompted to complete a system check.",
          item5: "Read and agree to the Terms & Conditions"
        }
      },
      q2: {
        question:
          "Can I navigate between questions or sections during the {{featureName}}?",
        firstSentence: "Navigation depends on the settings.",
        list: {
          item1:
            "If section navigation is enabled, you can move between sections.",
          item2:
            "If disabled, you'll need to complete one section before moving to the next."
        },
        lastSentence:
          "Instructions will be available at all times during the {{featureName}}."
      },
      q3: {
        question: "Will I be monitored during the test?",
        list: {
          item1:
            "If this {{featureName}} has video proctoring enabled, your camera and microphone will be used.",
          item2: "Any unusual activity may be flagged for review."
        }
      },
      q4: {
        question:
          "What happens if I lose internet connection or close the browser accidentally?",
        firstSentence: "Your progress is automatically saved.",
        lastSentence:
          "If disconnection occurs, you can rejoin the {{featureName}} using the same link."
      },
      q5: {
        question: "Will I get to see my results or feedback after the test?",
        firstSentence:
          "Results and feedback are configured by the {{featureName}} organizer.",
        list: {
          item1:
            "If enabled, you'll see your score and feedback summary after submission.",
          item2:
            "If not, your results will be shared through your registered email."
        }
      }
    },
    labels: {
      totalDuration: "Total Duration",
      numberOfQuestions: "No. Of Questions",
      numberOfSections: "No. Of Sections",
      guidelines: "Guidelines",
      platformHelp: "Platform Help",
      faq: "FAQ",
      frequentlyAskedQuestions: "Frequently Asked Questions",
      showFeatureInstructions: "Show {{featureName}} Instructions",
      minutes: "minutes",
      welcome: "Welcome",
      welcomeBack: "Welcome back",
      userFallback: "User",
      defaultGuidelines:
        "Before starting the {{featureName}}, ensure you are in a quiet, distraction-free space with a stable internet connection. Keep your camera on throughout the test, and manage your time wisely as there is a time limit. If you encounter any technical issues, contact support immediately. Once finished, review your answers before submitting. Good luck!"
    },
    zoom: {
      textSize: "Text Size:",
      decreaseTextSize: "Decrease (A-)",
      resetTextSize: "Reset (A)",
      increaseTextSize: "Increase (A+)"
    },
    wrapper: {
      warningTitle: "Warning!",
      stayOnPageMessage:
        "Please stay on this page until you complete the {{featureName}}.",
      iUnderstand: "I Understand",
      submitTheFeature: "Submit The {{featureName}}",
      videoProctoringInterrupted: "Video Proctoring Interrupted",
      continuationBlocked:
        "Assessment continuation is blocked until secure video proctoring is restored.",
      retryProctoring: "Retry Proctoring"
    },
    progress: {
      proceed: "Proceed",
      resumeFeature: "Resume {{featureName}}",
      timeRemainingMessage:
        "remaining in the {{featureName}}. Click below to pick up where you left off.",
      continueOnThisDevice: "Continue on This Device?",
      switchToThisTab: "Switch to This Tab?",
      continueYourAssessmentHere: "Continue Your Assessment Here?",
      switchHere: "Switch Here",
      attemptsUnlimited:
        "You have unlimited attempts available for this assessment.",
      attemptsSingle:
        "You have <strong>1</strong> attempt available for this assessment.",
      attemptsMultiple:
        "You have <strong>{{remainingAttempts}}</strong> out of <strong>{{maxAttempts}}</strong> attempts available for this assessment.",
      coolOffAfterAttempt:
        "After each attempt, you'll need to wait <strong>{{coolOffTime}}</strong> before starting your next attempt."
    },
    loginWindow: {
      expiredTitle: "Login Time Expired",
      expiredMessage:
        "The login window for this {{featureName}} is now closed. You missed the allowed time to access the test."
    },
    coding: {
      saving: "Saving",
      offlineTitle: "Looks like you're offline",
      offlineDescription: "Changes won't sync until you reconnect",
      connectionRestored: "Connection restored",
      syncingLatest: "Syncing your latest changes...",
      autoSaveEnabled: "Auto Save Enabled",
      runOnlyChecksDescription: "only checks your code against test cases.",
      submitEvaluationDescription:
        "your code to have it evaluated and receive marks.",
      reset: "Reset",
      run: "Run",
      runTests: "Running Tests",
      submit: "Submit",
      submitting: "Submitting",
      resetCode: "Reset Code",
      resetCodeMessage:
        "Are you sure you want to reset? This will remove all your current code and restore the starter code",
      testCases: "Test Cases",
      testCase: "Test Case {{index}}",
      testCasePassed: "Test Case Passed",
      testCaseFailed: "Test Case Failed",
      runtimeError: "Runtime Error",
      timeLimitExceeded: "Time Limit Exceeded",
      timeLimitExceededTitle: "Time limit exceeded",
      timeLimitExceededDescription:
        "Your code did not execute within the time limits. Please optimize your code.",
      input: "Input",
      expectedOutput: "Expected Output",
      yourOutput: "Your Output",
      hiddenTestCase: "Hidden Test Case",
      hiddenTestCaseDescription:
        "Hidden test cases are used to evaluate if your code can handle different scenarios including corner cases",
      explanation: "Explanation",
      hints: "Hints"
    },
    subjective: {
      minWordsRequired: "Minimum 40 words are required to submit the response.",
      enterYourAnswer: "Enter your answer",
      submitResponse: "Submit Response",
      submittingResponse: "Submitting Response...",
      resetAnswer: "Reset Answer",
      resetAnswerMessage:
        "Are you sure you want to reset? Your current answer will be removed permanently.",
      retake: "Retake",
      showHint: "Show Hint",
      recording: "RECORDING"
    },
    systemCheck: {
      calibrate: "Calibrate",
      calibrating: "Calibrating...",
      accessDenied: "Access Denied",
      cameraPermissionDenied:
        "Permission denied for camera. Please enable camera access in your browser settings to continue.",
      errorFetchingDevices: "Error fetching devices",
      failedToStartAssessment: "Failed To Start The Assessment",
      unableToStartSecureProctoring:
        "Unable to start secure proctoring right now. Please retry.",
      unnamedCamera: "Unnamed Camera",
      facingCalibrationIssues: "Facing Issues with video calibration?",
      lookAtCenter: "Please look at the center of the screen and remain still.",
      unableToCalibrateTitle:
        "Unable to Calibrate Your Camera for Video Proctoring?",
      statusMessages: {
        readyForCalibration:
          "Make sure your face is clearly visible and centered on the screen. When ready, click 'Calibrate' to begin.",
        calibratingInProgress:
          "Calibration in progress. Keep looking at the screen.",
        calibratingTimedOut:
          "Camera Calibration timed out. Please refresh your browser to continue.",
        preparingCameraPreview:
          "Preparing camera stream and face tracking for calibration.",
        retryRequired: "Proctoring setup needs your action before continuing.",
        proctoringUnavailable:
          "Proctoring is currently unavailable and assessment cannot start securely.",
        calibrationCompleted:
          "Calibration successful! You're all set to start the assessment.",
        timeoutHelp:
          "If you still see this message, check for other tabs/apps using the camera or an external shutter."
      },
      failureReasons: {
        cameraPermissionDenied:
          "Camera permission is blocked. Allow access and retry.",
        cameraUnavailable:
          "Camera is unavailable. Check your device selection and retry.",
        facemeshInitFailed: "Face tracking setup failed. Retry to continue.",
        cocoLoadFailed:
          "Proctoring engine is unavailable. Refresh and try again.",
        streamLostReacquireFailed:
          "Camera stream recovery failed. Retry proctoring.",
        calibrationInsufficientData:
          "Calibration samples were insufficient. Retry calibration.",
        detectionCadenceUnavailable:
          "Timing configuration is not ready. Retry shortly."
      },
      calibrationInfo: {
        heading:
          "To successfully calibrate your video, please follow these steps:",
        followUpHeading: "If you still face the issue, follow these steps:",
        initialSteps: {
          step1: {
            title: "Ensure good lighting",
            description:
              "Sit in a well-lit area with your face clearly visible. Avoid bright light or windows behind you."
          },
          step2: {
            title: "Face the camera directly",
            description:
              "Position your face in the center of the frame and look straight into the camera during calibration."
          },
          step3: {
            title: "Avoid dark or blurry backgrounds",
            description: "Make sure your background is clear and not too dark."
          },
          step4: {
            title: "Check camera permissions",
            description:
              "Allow your browser to access the webcam when prompted."
          },
          step5: {
            title: "Stay still and look at the screen",
            description:
              "Keep your head steady during calibration to help the system detect your face properly."
          }
        },
        laterSteps: {
          step1: "Try refreshing the page and recalibrating your camera.",
          step2: "Close other apps using the webcam (like Zoom or Teams).",
          step3:
            "If the problem continues contact {{contactEmail}} with a screenshot of the error."
        }
      }
    },
    stepper: {
      termsAndConditions: "Terms and Conditions",
      agreeTerms: "I have read and agree to the terms and conditions.",
      previousStep: "Previous Step",
      nextStep: "Next Step",
      startAssessment: "Start Assessment",
      resumeAssessment: "Resume Assessment",
      mediaCheck: "Media Check",
      systemCheck: "System Check",
      audioCheck: {
        intro:
          "You can record a short video here to verify that your camera and microphone are working as expected",
        mediaPermissionDenied:
          "Camera or microphone access denied. Please enable permissions in your browser settings.",
        startRecording: "Start Recording",
        stopRecording: "Stop Recording",
        unnamedMicrophone: "Unnamed Microphone"
      },
      stepLabel: "STEP {{step}}",
      personalDetails: "Personal Details",
      emailId: "Email ID",
      firstName: "First Name",
      lastName: "Last Name",
      defaultTncStatement:
        "By participating in this assessment, you confirm that you meet the eligibility requirements specified by the company and agree to complete the test independently, without external assistance. The assessment is subject to video proctoring, and you must have your camera on throughout the session in a distraction-free environment. All content and materials provided during the assessment are confidential and must not be shared, reproduced, or distributed. You agree to abide by the designated time limit for the assessment; failure to submit before the deadline may result in disqualification. The company is not liable for any technical issues related to your internet connection, software, or hardware. Personal data collected during the assessment will be processed for evaluation and recruitment purposes only, in accordance with the company's privacy policy. Any attempt to cheat, plagiarize, or otherwise engage in unethical behavior will lead to immediate disqualification. By submitting your answers, you confirm that they are final, and no revisions can be made after submission. The company reserves the right to modify or update these terms at any time, and participants will be notified of any significant changes.",
      okay: "Okay"
    },
    access: {
      cancelledTitle: '"{{title}}" has been cancelled',
      cancelledDescription:
        "This assessment is no longer active and cannot be accessed. Please review your dashboard for other assigned or upcoming assessments.",
      goToHome: "Go to Home",
      refreshPage: "Refresh Page"
    },
    errorPage: {
      goBack: "Go Back",
      raiseRequest: "Raise Request",
      retryLogin: "Retry Login",
      contactUs: "Contact Us",
      goBackToEvent: "Go Back to Event",
      launchCourse: "Launch Course",
      created: "Created",
      starts: "Starts",
      ends: "Ends",
      untitled: "Untitled",
      serviceUnavailableTitle: "Oops! Something went wrong on our end",
      pageNotFoundTitle: "We can't seem to find the page you're looking for",
      forbiddenTitle: "Sorry, But you are not authorized to view this page",
      userNotFoundTitle: "Oops! We Can't Find Your User Details",
      troubleshootingIntro:
        "Below are some troubleshooting ideas you can try to get back on track.",
      serviceUnavailableStep1:
        "The error might be temporary. Wait a moment and refresh the page to see if the issue resolves.",
      serviceUnavailableStep2:
        "Clear your browser's cache and cookies. Cached data might be causing conflicts with the server.",
      serviceUnavailableStep3:
        "Ensure your internet connection is stable. A weak or disrupted connection can sometimes trigger server errors.",
      pageNotFoundStep1:
        "Ensure you're connected to the internet and your network is stable. Try reloading the page to see if the issue resolves.",
      pageNotFoundStep2:
        "Double-check the URL you entered for any typos or errors. Ensure it matches the intended destination.",
      pageNotFoundStep3:
        "If you're using a VPN, make sure it's connected and configured properly. Reconnect if necessary and try accessing the page again.",
      forbiddenStep1:
        "Confirm that your account has the appropriate permissions to access the requested resource. Contact your administrator for assistance.",
      forbiddenStep2:
        "Double-check the URL you entered for any typos or errors. Ensure it matches the intended destination.",
      forbiddenStep3:
        "If you're using a VPN, make sure it's connected and configured properly. Reconnect if necessary and try accessing the page again.",
      userNotFoundStep1:
        "Please try logging in again using your Single Sign-On (SSO) credentials to ensure there was no temporary connectivity issue.",
      userNotFoundStep2:
        "Verify that your SSO account is correctly configured and active. Ensure you are using the correct organizational credentials.",
      userNotFoundStep3:
        "If the problem persists, you may not have access to the platform. Please contact the application administrator for assistance.",
      courseClosedMessage:
        "You have closed the course window, click on the launch button to relaunch the course",
      assessmentCompletedMessage:
        "Your assessment has been completed, please go back to content to know your score"
    },
    sidebar: {
      home: "Home",
      quickAccess: "Quick Access",
      questionPaperAuthoring: "Question Paper Authoring",
      assessmentLibrary: "Assessment Library",
      scheduleInterview: "Schedule Interview",
      generateQuestions: "Generate Questions",
      roleManagement: "Role Management",
      logout: "Logout",
      noAccess: "No access",
      noAccessWithLabel: "{{label}} (No access)",
      noAccessToLink: "You don't have access to this link",
      expandSidebar: "Expand sidebar",
      collapseSidebar: "Collapse sidebar",
      expand: "Expand",
      collapse: "Collapse",
      myPreferences: "My Preferences"
    },
    timesUp: {
      title: "Assessment Time Completed",
      autoSubmittedMessage:
        "The timer has expired. Your answers have been saved and the {{featureName}} has been submitted automatically."
    },
    resultCard: {
      title: "{{featureName}} Results",
      score: "Score",
      passPercentage: "Pass Percentage",
      marksAway:
        "You are {{count}} marks away from certification. Focus on the recommended areas and try again.",
      certificationEarned:
        "You exceeded the certification requirements and earned your certification",
      congratulationsOnEarning:
        "Congratulations on Earning the {{featureName}} certification",
      almostThereTitle:
        "You're Closer Than you Think to Earning the Certification",
      certificatePassedDescription:
        "You've demonstrated the knowledge and skills required to achieve this certification. Take a moment to celebrate this accomplishment and share it with your network",
      certificateFailDescription:
        "This attempt highlighted opportunities to strengthen your skills. Review the recommended areas and return with confidence",
      congratsImageAlt: "Congrats Image",
      almostThereImageAlt: "Almost there Image"
    },
    sectionCard: {
      section: "Section",
      startSection: "Start Section",
      continueSection: "Continue Section",
      completed: "Completed",
      viewSection: "View Section",
      totalQuestions: "Total Questions:",
      sectionNotFound: "Section not found.",
      total: "Total",
      flagged: "Flagged"
    },
    sectionSummary: {
      totalQuestions: "Total Questions",
      completedQuestions: "Completed Questions",
      flaggedQuestions: "Flagged Questions",
      unattemptedQuestions: "Unattempted Questions",
      codingQuestions: "Coding Questions:",
      objectiveQuestions: "Objective Questions:",
      goToSections: "Go to sections",
      lowWordCountWarning:
        "Your answer for the question, {{questionTitle}} is less than 40 words. If you go back to the section list, this response will get discarded.",
      videoNotSubmittedWarning:
        "Your recorded video for the question, {{questionTitle}} is not submitted. If you go back to the section list, this response will get discarded."
    },
    objective: {
      questionCounter: "Question",
      marks: "Marks :",
      explanation: "Explanation",
      hints: "Hints",
      reset: "Reset",
      flag: "Flag",
      unflag: "Unflag",
      previous: "Previous",
      saveAndNext: "Save & Next",
      saveAndViewSummary: "Save and View Section Summary",
      next: "Next",
      viewSectionSummary: "View Section Summary",
      hideExplanation: "Hide Explanation",
      showExplanation: "Show Explanation",
      hintWaitTooltip: "You need to wait before you see another hint",
      noHintsPendingTooltip: "No hints are pending",
      selectOptionForExplanation:
        "Select option to view the explanation of selected option"
    },
    tour: {
      shared: {
        timer:
          "This timer displays the remaining time for your assessment. Please monitor it closely to ensure you complete all sections within the allotted time.",
        submit:
          "Once you have completed all sections of the assessment, you can submit your work by clicking the 'Submit Assessment' button. This action finalizes your responses, and you will no longer be able to make changes once submitted. Ensure that you have reviewed all your answers before proceeding with the submission."
      },
      dashboard: {
        legend:
          "The section status legend helps you understand your progress throughout the assessment. It uses clear indicators to show whether a section has not been started, is currently in progress, or has been completed. This allows you to quickly assess which parts still require your attention and manage your time effectively.",
        sectionCard:
          "The section card provides a detailed overview of each assessment section. It displays the section title, its current status, and key information about the questions within the section. This includes the total number of questions, the number of flagged questions, and the number of questions that have been completed. The section card helps you monitor your progress and identify areas that may need more attention."
      },
      coding: {
        titles:
          "At the top of the page, you'll find a clear display of the assessment title and the section title. This helps you keep track of your current task and ensures that you are aware of the specific section you are working on. Refer to these titles to stay oriented as you navigate through the questions.",
        sectionsButton:
          "This button allows you to easily navigate back to the sections dashboard. Use it to return to the main overview of your assessment, where you can view and manage the progress of all sections. It provides a quick way to reassess your overall progress without losing any of your current work.",
        sidebar:
          "The sidebar provides an overview of all the questions within the current section you are attempting. It allows you to quickly navigate between questions by simply selecting the relevant block. Each question block is color-coded based on its status, making it easy to track your progress and jump to specific questions as needed.",
        questionCard:
          "This card contains the question you are currently working on, displaying the full prompt and any relevant instructions or input requirements. It is designed to give you a clear view of the task at hand, with all the necessary information to proceed. You will also find a button to flag the question for later review, allowing you to easily revisit it if needed. Additionally, there are navigation buttons that allow you to move to the next or previous question without returning to the sidebar, ensuring smooth and efficient progression through the assessment.",
        editor:
          "The code editor is where you will write and modify your solutions. You can switch between different programming languages to match the requirements of each question. Additionally, you have the option to adjust the editor's theme for a more comfortable coding experience. If you prefer more screen space, the editor can be expanded to full width, giving you a larger area to work with. These features are designed to provide a flexible and user-friendly environment as you complete your coding tasks."
      }
    },
    toast: {
      proctoringViolationDetected: "Proctoring Violation Detected",
      messages: {
        forbiddenObjectDetected: "Forbidden object detected",
        noFaceDetected: "No face detected",
        multipleFacesDetected: "Multiple faces detected",
        lookingAwayFromScreen: "Looking away from screen",
        notLookingAtScreen: "Not looking at screen",
        faceNotOrientedCorrectly: "Face not oriented correctly",
        eyesNotFullyOpen: "Eyes not fully open",
        copyNotAllowed: "Copying is not allowed",
        cutRestricted: "Cutting is restricted in proctoring mode",
        pasteNotAllowed: "Pasting is not allowed",
        contextMenuNotAllowed: "Opening of context menu is not allowed"
      }
    },
    whatsNext: {
      heading: "What's Next?",
      attemptsRemaining: "attempts remaining",
      passed: {
        shareLinkedInTitle: "Share on LinkedIn",
        shareLinkedInSubtitle:
          "Celebrate your achievement with your professional network.",
        viewCertificateTitle: "View Certificate",
        viewCertificateSubtitle:
          "Download or access your certification anytime.",
        shareGloTitle: "Share Experience",
        shareGloSubtitle:
          "Inspire colleagues by posting your achievement internally."
      },
      failed: {
        reviewLearningResourcesTitle: "Review Learning Resources",
        reviewLearningResourcesSubtitle:
          "Strengthen the skill that needs attention.",
        retakeAssessmentTitle: "Retake Assessment",
        retakeAssessmentSubtitle:
          "Demonstrate your progress and updated understanding."
      },
      modal: {
        title: "Share Achievement",
        linkedInPostLabel: "Share on LinkedIn as Post",
        linkedInProfileLabel: "Add as a Certification to LinkedIn Profile",
        gloLabel: "Share on Glo",
        comingSoon: "Coming soon",
        infoMessage: "You must be logged in to the platform to share."
      }
    },
    time: {
      hour_one: "{{count}} hour",
      hour_other: "{{count}} hours",
      minute_one: "{{count}} minute",
      minute_other: "{{count}} minutes",
      hourShort: "H",
      minuteShort: "M",
      secondShort: "S"
    },
    accessScreens: {
      attemptResultProcessing: {
        alt: "{{featureName}} results are being processed",
        title: "Your Results Are Being Processed",
        description:
          "We are still reviewing your {{featureName}}. Please check back shortly!"
      },
      coolOffPeriod: {
        alt: "Cool off period",
        title: "Next Attempt Available Soon",
        completedAttempt: "You have already completed an attempt for this",
        nextAttemptIntro: ". Your next attempt will be available in"
      },
      maxAttemptExceeded: {
        alt: "Maximum attempts reached",
        title: "Maximum Attempts Reached",
        description:
          "You've used all available attempts for this {{featureName}}. No further attempts can be made."
      },
      scheduleNotStarted: {
        alt: "Schedule not started",
        title: "{{featureName}} Not Started Yet",
        scheduledPrefix: "The",
        scheduledOn: "is scheduled to begin on",
        checkBack:
          "Please check back in {{time}} or refresh once the time starts."
      },
      scheduleTimeCompleted: {
        alt: "Schedule time completed",
        title: "{{featureName}} Window Closed",
        description:
          "The scheduled time for this {{featureName}} has passed. You can no longer start or submit an attempt."
      }
    },
    attemptDetails: {
      allAttempts: "All Attempts",
      headers: {
        attempt: "Attempt",
        attemptedOn: "Attempted on",
        percentage: "Percentage"
      },
      viewAllAttempts: "View All Attempts"
    },
    survey: {
      questions: {
        q1: "On a scale of 1 to 5, how clear did you find the {{featureName}} questions?",
        q2: "I felt adequately prepared for the topics discussed in the {{featureName}}",
        q3: "The platform used for the {{featureName}} was user-friendly and functional",
        q4: "I believe my responses effectively showcased my skills and knowledge"
      },
      stronglyDisagree: "Strongly Disagree",
      stronglyAgree: "Strongly Agree",
      submitFeedback: "Submit Feedback"
    },
    cameraPreview: {
      loadingAlt: "Camera preview loading",
      delayedMessage:
        "Calibration is taking longer than expected due to a slow network. Please wait or try refreshing the page."
    },
    proctoring: {
      engineLoadFailedStatus: "Proctoring engine failed to load",
      runtimeErrors: {
        cameraPermissionDenied:
          "Camera access is blocked by browser permission settings.",
        cameraUnavailable:
          "Camera is unavailable or currently in use by another application.",
        unableToLoadModel: "Unable to load proctoring object-detection model.",
        webglRecoveryFailed:
          "WebGL recovery failed while restoring object detection."
      },
      logs: {
        engineLoadFailed: "ProctoringEngine failed to load:",
        videoPlayRejected: "Video play() rejected:",
        cameraTrackEnded: "Camera track ended - attempting re-acquisition",
        cameraReacquired: "Camera re-acquired on attempt {{attempt}}",
        cameraReacquireAttemptFailed:
          "Camera re-acquisition attempt {{attempt}} failed:",
        cameraReacquireFailedAll:
          "Camera re-acquisition failed after all attempts - reporting cameraDisconnected each cycle",
        uploadFailedQueued:
          "Proctoring upload failed after {{attempts}} attempts, queuing for retry:",
        uploadAttemptFailedRetrying:
          "Proctoring upload attempt {{attempt}} failed, retrying...",
        drainedQueuedViolation:
          "Drained queued violation: {{violationType}} ({{remaining}} remaining)",
        queueDrainFailed: "Queue drain failed, will retry next cycle:",
        cocoLoadFailed: "COCO-SSD load failed:",
        faceProcessingError: "Face processing error:",
        errorProcessingFrame: "Error processing frame:",
        faceMeshInitError: "Face Mesh initialization error:"
      }
    }
  }
} as const;

export default en;
